import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Pagination,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { useQuery } from "@tanstack/react-query";
import useDebounce from "hooks/useDebounce";
import useResettableState from "hooks/useResettableState";
import { useToast } from "components/Toast";
import {
  ActivityQuery,
  FinanceQuery,
  exportFinance,
  formatNaira,
  getApiError,
  getFinanceActivity,
} from "services/finance";

const LIMIT = 10;

const formatDate = (value: string) => {
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const headCellSx = {
  bgcolor: "#F2F4F7",
  color: "#475467",
  fontSize: 12,
  fontWeight: 600,
  borderBottom: "none",
  whiteSpace: "nowrap",
};

const ActivityTable = ({ query }: { query: FinanceQuery }) => {
  const { showToast } = useToast();
  const [type, setType] = useState<"all" | "revenue" | "expense">("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), 400);

  // Reset to the first page whenever the filters change.
  const [page, setPage] = useResettableState(
    1,
    [type, debouncedSearch, query.period, query.start, query.end, query.product].join("|"),
  );

  const params: ActivityQuery = {
    ...query,
    type,
    search: debouncedSearch,
    page,
    limit: LIMIT,
  };

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["finance", "activity", params],
    queryFn: () => getFinanceActivity(params),
    placeholderData: (prev) => prev,
  });

  const items = data?.data?.items || [];
  const pagination = data?.data?.pagination;
  const totalPages =
    pagination?.pages ??
    pagination?.totalPages ??
    Math.max(1, Math.ceil((pagination?.total || 0) / LIMIT));

  const handleExport = async () => {
    try {
      setExporting(true);
      await exportFinance({ ...query, type, search: debouncedSearch });
    } catch (error) {
      showToast(getApiError(error, "Failed to export CSV"), "error");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box bgcolor="#FFFFFF" border="1px solid #E4E7EC" borderRadius="12px" p="24px">
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap="12px"
        mb="20px"
      >
        <Typography fontSize={16} fontWeight={600} color="#101828">
          Recent Financial Activity
        </Typography>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap="12px"
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <TextField
            size="small"
            placeholder="Search FIN ref, description, PNR..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: { sm: 260 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#98A2B3" }} />
                </InputAdornment>
              ),
            }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={type}
            onChange={(_, v) => v && setType(v)}
            sx={{
              "& .MuiToggleButton-root": {
                border: "none",
                textTransform: "none",
                px: "16px",
                flex: { xs: 1, sm: "initial" },
                color: "#475467",
                borderRadius: "6px !important",
              },
              "& .Mui-selected": {
                bgcolor: "#F2F4F7 !important",
                color: "#101828 !important",
                fontWeight: 600,
              },
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="revenue">Revenue</ToggleButton>
            <ToggleButton value="expense">Expenses</ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{
              textTransform: "none",
              color: "#0b1c12",
              borderColor: "#D0D5DD",
              whiteSpace: "nowrap",
            }}
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ overflowX: "auto", opacity: isFetching && !isLoading ? 0.6 : 1 }}>
        <Table sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, borderRadius: "8px 0 0 8px" }}>Reference</TableCell>
              <TableCell sx={headCellSx}>Description</TableCell>
              <TableCell sx={headCellSx}>Category</TableCell>
              <TableCell sx={headCellSx}>Type</TableCell>
              <TableCell sx={headCellSx} align="right">
                Amount
              </TableCell>
              <TableCell sx={{ ...headCellSx, borderRadius: "0 8px 8px 0" }} align="right">
                Date
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [0, 1, 2, 3, 4].map((i) => (
                <TableRow key={i}>
                  <TableCell colSpan={6}>
                    <Skeleton height={28} />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography textAlign="center" color="#98A2B3" fontSize={14} py="24px">
                    No financial activity found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => {
                const isRevenue = item.type === "Revenue";
                const voided = item.status === "voided";
                return (
                  <TableRow key={item.id} sx={{ opacity: voided ? 0.5 : 1 }}>
                    <TableCell sx={{ color: "#2F6B4F", fontSize: 14, whiteSpace: "nowrap" }}>
                      {item.reference}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: "#101828" }}>
                      {item.description}
                      {item.bookingRef && (
                        <Typography fontSize={12} color="#98A2B3">
                          {item.bookingRef}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 14, color: "#475467" }}>
                      {item.categoryLabel || item.category}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={voided ? "Voided" : item.type}
                        sx={{
                          borderRadius: "6px",
                          fontSize: 12,
                          fontWeight: 600,
                          bgcolor: voided ? "#F2F4F7" : isRevenue ? "#ECFDF3" : "#FEF3F2",
                          color: voided ? "#667085" : isRevenue ? "#12B76A" : "#D92D20",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        color: item.amount < 0 ? "#D92D20" : "#12B76A",
                        textDecoration: voided ? "line-through" : "none",
                      }}
                    >
                      {formatNaira(item.amount)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontSize: 14, color: "#475467", whiteSpace: "nowrap" }}
                    >
                      {formatDate(item.date)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt="20px">
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default ActivityTable;
